export const routePatterns = {
  login: '/',
  queue: '/queue',
  task: '/task/:taskId',
  admin: '/admin',
  adminCampaign: '/admin/campaigns/:campaignId',
  adminTool: '/admin/:tool',
  adminTaskType: '/admin/task-types/:taskTypeId',
  adminNexusUpload: '/admin/google-nexus/uploads/:uploadId',
  instructions: '/task-types/:taskTypeId/instructions',
  browseTasks: '/admin/browse-tasks',
  settings: '/settings',
  notFound: '*',
} as const;

export type RouteArea = 'public' | 'workspace' | 'admin';

export interface RouteMetadata {
  id: string;
  path: string;
  area: RouteArea | 'fallback';
}

/**
 * Route metadata is intentionally kept beside the builders. It gives tests and
 * future tooling one inventory of the URL surface without importing pages.
 */
export const routeMetadata: readonly RouteMetadata[] = [
  { id: 'login', path: routePatterns.login, area: 'public' },
  { id: 'queue', path: routePatterns.queue, area: 'workspace' },
  { id: 'task', path: routePatterns.task, area: 'workspace' },
  { id: 'admin', path: routePatterns.admin, area: 'admin' },
  { id: 'adminCampaign', path: routePatterns.adminCampaign, area: 'admin' },
  { id: 'adminTool', path: routePatterns.adminTool, area: 'admin' },
  { id: 'adminTaskType', path: routePatterns.adminTaskType, area: 'admin' },
  { id: 'adminNexusUpload', path: routePatterns.adminNexusUpload, area: 'admin' },
  { id: 'instructions', path: routePatterns.instructions, area: 'workspace' },
  { id: 'browseTasks', path: routePatterns.browseTasks, area: 'admin' },
  { id: 'settings', path: routePatterns.settings, area: 'workspace' },
  { id: 'notFound', path: routePatterns.notFound, area: 'fallback' },
];

export function assertUniqueRouteMetadata(metadata: readonly RouteMetadata[] = routeMetadata): void {
  const ids = new Set<string>();
  const paths = new Set<string>();
  for (const route of metadata) {
    if (ids.has(route.id)) throw new Error(`Duplicate route id: ${route.id}`);
    if (paths.has(route.path)) throw new Error(`Duplicate route path: ${route.path}`);
    ids.add(route.id);
    paths.add(route.path);
  }
}

function appendQuery(path: string, query?: string): string {
  const normalized = query?.replace(/^\?/, '').trim();
  return normalized ? `${path}?${normalized}` : path;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

/** The only module that should construct internal application URLs. */
export const paths = {
  login: (redirect?: string | null): string => (
    redirect ? `${routePatterns.login}?redirect=${encodeURIComponent(redirect)}` : routePatterns.login
  ),
  queue: (): string => routePatterns.queue,
  task: (taskId: string): string => `/task/${segment(taskId)}`,
  admin: (): string => routePatterns.admin,
  adminCampaign: (campaignId: string): string => `/admin/campaigns/${segment(campaignId)}`,
  adminTool: (slug: string): string => `/admin/${segment(slug)}`,
  adminTaskType: (taskTypeId: string): string => `/admin/task-types/${segment(taskTypeId)}`,
  adminNexusUpload: (uploadId: string): string => `/admin/google-nexus/uploads/${segment(uploadId)}`,
  instructions: (taskTypeId: string): string => `/task-types/${segment(taskTypeId)}/instructions`,
  browseTasks: (query?: string): string => appendQuery(routePatterns.browseTasks, query),
  settings: (): string => routePatterns.settings,
};
